import { useState, useEffect, useRef, useCallback } from "react";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";

// Leaflet loaded via CDN script tag injected once
let leafletReady = false;
function ensureLeaflet(cb) {
    if (window.L) { cb(); return; }
    if (leafletReady) { const t = setInterval(() => { if (window.L) { clearInterval(t); cb(); } }, 50); return; }
    leafletReady = true;
    const css = document.createElement("link");
    css.rel = "stylesheet"; css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    js.onload = cb;
    document.head.appendChild(js);
}


// ── Coordinate graticule (shared) ────────────────────────────────────────────
function drawGraticule(lmap, grpLayer) {
    grpLayer.clearLayers();
    const L = window.L;
    const bounds = lmap.getBounds();
    const z = lmap.getZoom();
    let sp = 0.5;
    if (z >= 14) sp = 0.005;
    else if (z >= 12) sp = 0.01;
    else if (z >= 10) sp = 0.025;
    else if (z >= 8)  sp = 0.05;
    else if (z >= 6)  sp = 0.1;
    const snap = v => Math.round(v * 1e6) / 1e6;
    const latMin = Math.floor(bounds.getSouth() / sp) * sp;
    const latMax = Math.ceil(bounds.getNorth()  / sp) * sp;
    const lngMin = Math.floor(bounds.getWest()  / sp) * sp;
    const lngMax = Math.ceil(bounds.getEast()   / sp) * sp;
    const lats = [], lngs = [];
    for (let v = latMin; v <= latMax; v = snap(v + sp)) lats.push(snap(v));
    for (let v = lngMin; v <= lngMax; v = snap(v + sp)) lngs.push(snap(v));
    const sty = { color: 'rgba(160,180,200,0.22)', weight: 0.5, dashArray: '3 7', interactive: false };
    lats.forEach(lat => L.polyline([[lat, lngMin], [lat, lngMax]], sty).addTo(grpLayer));
    lngs.forEach(lng => L.polyline([[latMin, lng], [latMax, lng]], sty).addTo(grpLayer));
}

// Catmull-Rom → smooth polyline via intermediate points
function catmullRom(points, tension = 0.5) {
    if (points.length < 2) return points;
    const out = [points[0]];
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];
        for (let t = 0.25; t <= 0.75; t += 0.25) {
            const t2 = t * t, t3 = t2 * t;
            const lat =
                0.5 * ((2 * p1[0]) + (-p0[0] + p2[0]) * t +
                    (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
                    (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
            const lng =
                0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * t +
                    (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
                    (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
            out.push([lat, lng]);
        }
        out.push(p2);
    }
    return out;
}

const COLORS = [
    "#ef4444","#f97316","#eab308","#22c55e","#3b82f6","#8b5cf6","#ec4899",
    "#ffffff","#94a3b8","#1e293b","#C29717","#06b6d4",
];

const TOOLS = [
    { id: "select",  icon: "near_me",       label: "Select / Move" },
    { id: "marker",  icon: "location_on",   label: "Marker" },
    { id: "pulse",   icon: "radio_button_checked", label: "Pulse Marker" },
    { id: "line",    icon: "show_chart",    label: "Polyline (straight)" },
    { id: "curve",   icon: "gesture",       label: "Curve (smooth)" },
    { id: "arrow",   icon: "arrow_forward", label: "Arrow line" },
    { id: "circle",  icon: "circle",        label: "Circle" },
    { id: "polygon", icon: "pentagon",      label: "Polygon" },
    { id: "rect",    icon: "crop_square",   label: "Rectangle" },
    { id: "text",    icon: "text_fields",   label: "Text label" },
];

const SAT_LAYERS = [
    { id: "dark",     label: "Flat Map",     icon: "dark_mode" },
    { id: "bing",     label: "Satellite", icon: "satellite" },
    { id: "esri",     label: "ESRI Sat", icon: "satellite_alt" },
    { id: "ocean",    label: "Ocean",    icon: "water" },
];

export default function MapBuilderEditor({ map, onBack, apiUrl }) {
    const mapDivRef = useRef(null);
    const leafletRef = useRef(null);   // { map, tileLayer, overlayGroup, draftGroup, draftPoly, draftPoints }
    const [tool, setTool] = useState("select");
    const [color, setColor] = useState("#ef4444");
    const [lineWeight, setLineWeight] = useState(1);
    const [lineStyle, setLineStyle] = useState("solid");
    const [fillOpacity, setFillOpacity] = useState(0.2);
    const [labelText, setLabelText] = useState("");
    const [satMode, setSatMode] = useState("dark");
    const [layers, setLayers] = useState(map.layers || []);
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showGrid, setShowGrid] = useState(true);
    const [mouseCoords, setMouseCoords] = useState(null);
    const [selectedIdx, setSelectedIdx] = useState(null);
    const toolRef = useRef(tool);
    const colorRef = useRef(color);
    const weightRef = useRef(lineWeight);
    const styleRef  = useRef(lineStyle);
    const fillRef = useRef(fillOpacity);
    const labelRef = useRef(labelText);
    const layersRef = useRef(layers);
    const layerItemRefs = useRef({});

    // keep refs in sync
    useEffect(() => { toolRef.current = tool; }, [tool]);
    useEffect(() => { colorRef.current = color; }, [color]);
    useEffect(() => { weightRef.current = lineWeight; }, [lineWeight]);
    useEffect(() => { styleRef.current = lineStyle; }, [lineStyle]);
    useEffect(() => { fillRef.current = fillOpacity; }, [fillOpacity]);
    useEffect(() => { labelRef.current = labelText; }, [labelText]);
    useEffect(() => { layersRef.current = layers; }, [layers]);

    // Track unsaved changes
    const savedLayersRef = useRef(JSON.stringify(map.layers || []));
    useEffect(() => {
        const changed = JSON.stringify(layers) !== savedLayersRef.current;
        setIsDirty(changed);
    }, [layers]);

    // ── tile helpers ──────────────────────────────────────────────────────────
    function buildTile(mode) {
        const L = window.L;
        if (mode === "dark") return L.layerGroup([
            L.tileLayer(
                "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
                { subdomains: "abcd", maxZoom: 19 }
            ),
            L.tileLayer(
                "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
                { subdomains: "abcd", maxZoom: 19 }
            ),
        ]);
        if (mode === "bing") {
            const BingTL = L.TileLayer.extend({
                getTileUrl(c) {
                    let q = "";
                    for (let i = c.z; i > 0; i--) {
                        let d = 0, m = 1 << (i - 1);
                        if (c.x & m) d++;
                        if (c.y & m) d += 2;
                        q += d;
                    }
                    const s = (c.x + c.y + c.z) % 4;
                    return `https://ecn.t${s}.tiles.virtualearth.net/tiles/a${q}.jpeg?g=6897`;
                }
            });
            return new BingTL("", { maxZoom: 19 });
        }
        if (mode === "esri") return L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            { maxZoom: 18 }
        );
        if (mode === "ocean") return L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}",
            { maxZoom: 16 }
        );
        if (mode === "navChart") return L.tileLayer(
            "https://server.arcgisonline.com/ArcGIS/rest/services/Specialty/World_Navigation_Charts/MapServer/tile/{z}/{y}/{x}",
            { maxZoom: 15 }
        );
        return L.layerGroup([
            L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", { subdomains: "abcd" }),
            L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}", { subdomains: "abcd" }),
        ]);
    }

    // ── render a single layer item as Leaflet object ──────────────────────────
    const renderLayer = useCallback((L, lyr, idx, overlayGroup) => {
        let obj = null;
        const clickHandler = (e) => {
            L.DomEvent.stopPropagation(e);
            setSelectedIdx(idx);
        };

        if (lyr.type === "marker" || lyr.type === "pulse") {
            const isPulse = lyr.type === "pulse";
            const html = isPulse
                ? `<div style="position:relative;width:16px;height:16px;">
                     <div style="position:absolute;inset:0;border-radius:50%;background:${lyr.color};opacity:0.35;animation:mb-pulse 1.5s ease-in-out infinite;"></div>
                     <div style="position:absolute;top:3px;left:3px;width:10px;height:10px;border-radius:50%;background:${lyr.color};"></div>
                   </div>${lyr.label ? `<div style="position:absolute;left:20px;top:-2px;white-space:nowrap;font-size:11px;font-family:monospace;color:${lyr.color};text-shadow:0 0 4px rgba(0,0,0,0.9);">${lyr.label}</div>` : ""}`
                : `<div style="position:relative;">
                     <svg width="26" height="32" viewBox="0 0 22 28">
                       <path d="M11 0C6.48 0 2 4.48 2 10c0 7.5 9 18 9 18s9-10.5 9-18C20 4.48 15.52 0 11 0z" fill="${lyr.color}" stroke="rgba(0,0,0,0.4)" stroke-width="1"/>
                       <circle cx="11" cy="10" r="4" fill="rgba(0,0,0,0.25)"/>
                     </svg>
                     ${lyr.label ? `<div style="position:absolute;left:24px;top:2px;white-space:nowrap;font-size:11px;font-family:monospace;color:${lyr.color};text-shadow:0 0 4px rgba(0,0,0,0.9);">${lyr.label}</div>` : ""}
                   </div>`;
            const icon = L.divIcon({ className: "", html, iconSize: [26, 32], iconAnchor: [13, 32] });
            obj = L.marker([lyr.lat, lyr.lng], { icon, draggable: true });
            obj.on("click", clickHandler);
            obj.on("dragend", (e) => {
                const ll = e.target.getLatLng();
                setLayers(prev => {
                    const next = [...prev];
                    next[idx] = { ...next[idx], lat: ll.lat, lng: ll.lng };
                    return next;
                });
            });
        } else if (lyr.type === "text") {
            const html = `<div style="white-space:nowrap;font-size:${lyr.fontSize||13}px;font-family:monospace;font-weight:600;color:${lyr.color};text-shadow:0 0 6px rgba(0,0,0,0.9),0 0 6px rgba(0,0,0,0.9);">${lyr.label}</div>`;
            const icon = L.divIcon({ className: "", html, iconSize: [null, null], iconAnchor: [0, 0] });
            obj = L.marker([lyr.lat, lyr.lng], { icon, draggable: true });
            obj.on("click", clickHandler);
            obj.on("dragend", (e) => {
                const ll = e.target.getLatLng();
                setLayers(prev => { const n = [...prev]; n[idx] = { ...n[idx], lat: ll.lat, lng: ll.lng }; return n; });
            });
        } else if (lyr.type === "line" || lyr.type === "curve" || lyr.type === "arrow") {
            const pts = lyr.smooth ? catmullRom(lyr.points) : lyr.points;
            obj = L.polyline(pts, {
                color: lyr.color, weight: lyr.weight || 2,
                opacity: 0.85, dashArray: lyr.dash || null,
            });
            if (lyr.type === "arrow" && pts.length >= 2) {
                const last = pts[pts.length - 1];
                const prev2 = pts[pts.length - 2];
                const angle = Math.atan2(last[0] - prev2[0], last[1] - prev2[1]) * 180 / Math.PI;
                const ah = L.divIcon({
                    className: "",
                    html: `<div style="transform:rotate(${angle}deg);color:${lyr.color};font-size:16px;line-height:1;text-shadow:0 0 3px rgba(0,0,0,0.6);">▶</div>`,
                    iconSize: [16, 16], iconAnchor: [8, 8],
                });
                L.marker(last, { icon: ah, interactive: false }).addTo(overlayGroup);
            }
            obj.on("click", clickHandler);
        } else if (lyr.type === "circle") {
            obj = L.circle([lyr.lat, lyr.lng], {
                radius: lyr.radius, color: lyr.color, weight: lyr.weight || 1.5,
                fillColor: lyr.color, fillOpacity: lyr.fillOpacity ?? 0.15,
            });
            obj.on("click", clickHandler);
        } else if (lyr.type === "polygon" || lyr.type === "rect") {
            obj = L.polygon(lyr.points, {
                color: lyr.color, weight: lyr.weight || 1.5,
                fillColor: lyr.color, fillOpacity: lyr.fillOpacity ?? 0.15,
            });
            obj.on("click", clickHandler);
        }
        if (obj) obj.addTo(overlayGroup);
        return obj;
    }, []);

    // ── redraw all layers ─────────────────────────────────────────────────────
    const redrawAll = useCallback((L, overlayGroup, currentLayers) => {
        overlayGroup.clearLayers();
        currentLayers.forEach((lyr, idx) => renderLayer(L, lyr, idx, overlayGroup));
    }, [renderLayer]);

    // ── init map ──────────────────────────────────────────────────────────────
    useEffect(() => {
        ensureLeaflet(() => {
            if (!mapDivRef.current || leafletRef.current) return;
            const L = window.L;

            // Inject pulse keyframes once
            if (!document.getElementById("mb-keyframes")) {
                const s = document.createElement("style");
                s.id = "mb-keyframes";
                s.textContent = "@keyframes mb-pulse{0%,100%{transform:scale(1);opacity:0.35}50%{transform:scale(2.2);opacity:0}}";
                document.head.appendChild(s);
            }

            const centre = (map.lat && map.lng) ? [+map.lat, +map.lng] : [26.1, 57.8];
            const lmap = L.map(mapDivRef.current, {
                center: centre, zoom: map.lat ? 9 : 7,
                zoomControl: true, attributionControl: false,
            });

            const tileLayer = buildTile("dark").addTo(lmap);
            const overlayGroup = L.layerGroup().addTo(lmap);
            const draftGroup = L.layerGroup().addTo(lmap);
            const graticuleGroup = L.layerGroup().addTo(lmap);
            const editGroup = L.layerGroup().addTo(lmap);

            const ref = { map: lmap, tileLayer, overlayGroup, draftGroup, graticuleGroup, editGroup, draftPoly: null, draftPoints: [], circleStart: null };
            leafletRef.current = ref;

            // ── Shared commit helper (used by dblclick and Enter key) ─────────
            const commitDraft = () => {
                const t = toolRef.current;
                const c2 = colorRef.current;
                const w2 = weightRef.current;
                const fo2 = fillRef.current;
                const dash2 = styleRef.current === 'dashed' ? '10 7' : styleRef.current === 'dotted' ? '2 6' : null;
                const pts = [...ref.draftPoints];
                if (pts.length < 2) { ref.draftPoints = []; ref.draftGroup.clearLayers(); return; }
                if (t === 'line' || t === 'curve' || t === 'arrow') {
                    const nl = { type: t, points: pts, color: c2, weight: w2, smooth: t === 'curve', dash: dash2 };
                    ref.draftGroup.clearLayers(); ref.draftPoints = [];
                    setLayers(prev => { const n = [...prev, nl]; redrawAll(L, overlayGroup, n); return n; });
                } else if (t === 'polygon' || t === 'rect') {
                    const nl = { type: pts.length === 2 ? 'rect' : 'polygon', points: pts, color: c2, weight: w2, fillOpacity: fo2, dash: dash2 };
                    ref.draftGroup.clearLayers(); ref.draftPoints = [];
                    setLayers(prev => { const n = [...prev, nl]; redrawAll(L, overlayGroup, n); return n; });
                }
            };
            ref.commitDraft = commitDraft;

            // ── Keyboard: Enter = commit, Escape = cancel ─────────────────────
            const onKey = (e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                    e.preventDefault();
                    if (toolRef.current === 'select') {
                        // exit edit mode — deselect selected layer
                        setSelectedIdx(null);
                    } else if (e.key === 'Enter') {
                        ref.commitDraft && ref.commitDraft();
                    } else {
                        // Escape while drawing — cancel draft
                        ref.draftPoints = []; ref.draftGroup.clearLayers();
                    }
                }
            };
            document.addEventListener('keydown', onKey);
            ref._onKey = onKey;

            ref._showGrid = true;
            redrawAll(L, overlayGroup, layersRef.current);
            drawGraticule(lmap, graticuleGroup);

            // ── Map click handler ─────────────────────────────────────────────
            lmap.on("click", (e) => {
                const t = toolRef.current;
                const c = colorRef.current;
                const w = weightRef.current;
                const fo = fillRef.current;
                const lbl = labelRef.current.trim();
                const { lat, lng } = e.latlng;

                if (t === "select") return;

                if (t === "marker" || t === "pulse") {
                    const newLayer = { type: t, lat, lng, color: c, label: lbl || null };
                    setLayers(prev => {
                        const next = [...prev, newLayer];
                        redrawAll(L, overlayGroup, next);
                        return next;
                    });
                    return;
                }

                if (t === "text") {
                    const txt = lbl || prompt("Label text:");
                    if (!txt) return;
                    const newLayer = { type: "text", lat, lng, color: c, label: txt, fontSize: 13 };
                    setLayers(prev => { const n = [...prev, newLayer]; redrawAll(L, overlayGroup, n); return n; });
                    return;
                }

                if (t === "circle") {
                    if (!ref.circleStart) {
                        ref.circleStart = { lat, lng };
                        L.circleMarker([lat, lng], { radius: 5, color: c, fillColor: c, fillOpacity: 0.8 }).addTo(draftGroup);
                    } else {
                        const R = lmap.distance([ref.circleStart.lat, ref.circleStart.lng], [lat, lng]);
                        const newLayer = { type: "circle", lat: ref.circleStart.lat, lng: ref.circleStart.lng, radius: R, color: c, weight: w, fillOpacity: fo };
                        draftGroup.clearLayers();
                        ref.circleStart = null;
                        setLayers(prev => { const n = [...prev, newLayer]; redrawAll(L, overlayGroup, n); return n; });
                    }
                    return;
                }

                if (t === "line" || t === "curve" || t === "arrow") {
                    ref.draftPoints.push([lat, lng]);
                    draftGroup.clearLayers();
                    L.circleMarker([lat, lng], { radius: 4, color: c, fillColor: c, fillOpacity: 0.9 }).addTo(draftGroup);
                    if (ref.draftPoints.length > 1) {
                        const pts = t === "curve" ? catmullRom(ref.draftPoints) : ref.draftPoints;
                        L.polyline(pts, { color: c, weight: w, dashArray: "5 4", opacity: 0.6 }).addTo(draftGroup);
                    }
                    return;
                }

                if (t === "polygon" || t === "rect") {
                    ref.draftPoints.push([lat, lng]);
                    draftGroup.clearLayers();
                    if (ref.draftPoints.length > 1) {
                        L.polygon([...ref.draftPoints], { color: c, weight: w, fillColor: c, fillOpacity: 0.1, dashArray: "4 3" }).addTo(draftGroup);
                    }
                    ref.draftPoints.forEach(p => L.circleMarker(p, { radius: 4, color: c, fillColor: c, fillOpacity: 0.9 }).addTo(draftGroup));
                    return;
                }
            });

            // Mouse coordinate display
            lmap.on('mousemove', (e) => setMouseCoords(e.latlng));
            lmap.on('mouseout', () => setMouseCoords(null));

            // Graticule on zoom/move
            lmap.on('zoomend moveend', () => {
                if (ref._showGrid) drawGraticule(lmap, ref.graticuleGroup);
            });

            // Double-click → commit line/polygon
            lmap.on("dblclick", (e) => {
                L.DomEvent.stopPropagation(e);
                const t = toolRef.current;
                const c = colorRef.current;
                const w = weightRef.current;
                const fo = fillRef.current;
                const pts = [...ref.draftPoints];
                if (pts.length < 2) { ref.draftPoints = []; draftGroup.clearLayers(); return; }

                if (t === "line" || t === "curve" || t === "arrow") {
                    const newLayer = { type: t, points: pts, color: c, weight: w, smooth: t === "curve" };
                    draftGroup.clearLayers();
                    ref.draftPoints = [];
                    setLayers(prev => { const n = [...prev, newLayer]; redrawAll(L, overlayGroup, n); return n; });
                } else if (t === "polygon" || t === "rect") {
                    const newLayer = { type: t === "rect" && pts.length === 2 ? "rect" : "polygon", points: pts, color: c, weight: w, fillOpacity: fo };
                    draftGroup.clearLayers();
                    ref.draftPoints = [];
                    setLayers(prev => { const n = [...prev, newLayer]; redrawAll(L, overlayGroup, n); return n; });
                }
            });
        });

        return () => {
            if (leafletRef.current?._onKey) document.removeEventListener('keydown', leafletRef.current._onKey);
            if (leafletRef.current?.map) {
                leafletRef.current.map.remove();
                leafletRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── switch satellite base layer ───────────────────────────────────────────
    const switchSat = useCallback((mode) => {
        if (!leafletRef.current) return;
        const { map: lmap, tileLayer } = leafletRef.current;
        const L = window.L;
        lmap.removeLayer(tileLayer);
        const newTile = buildTile(mode);
        newTile.addTo(lmap);
        leafletRef.current.tileLayer = newTile;
        setSatMode(mode);
    }, []);

    // ── save ──────────────────────────────────────────────────────────────────
    const save = async () => {
        setSaving(true);
        try {
            await axios.put(apiUrl + "/map-builder/" + String(map._id), { layers });
            setSavedMsg(true);
            savedLayersRef.current = JSON.stringify(layers);
            setIsDirty(false);
            setTimeout(() => setSavedMsg(false), 2000);
        } catch (_) {}
        setSaving(false);
    };

    // ── delete selected layer ─────────────────────────────────────────────────
    const deleteLayer = (idx) => {
        setLayers(prev => {
            const next = prev.filter((_, i) => i !== idx);
            if (leafletRef.current) redrawAll(window.L, leafletRef.current.overlayGroup, next);
            return next;
        });
        setSelectedIdx(null);
    };


    // ── Vertex edit handles (shown when a line/polygon/curve is selected) ────
    useEffect(() => {
        if (!leafletRef.current) return;
        const { editGroup } = leafletRef.current;
        const L = window.L;
        if (!editGroup) return;
        editGroup.clearLayers();
        if (selectedIdx === null) return;
        const lyr = layers[selectedIdx];
        if (!lyr || !lyr.points) return;

        lyr.points.forEach((pt, pi) => {
            const vtxIcon = L.divIcon({
                className: "",
                html: "<div style=\"width:12px;height:12px;border-radius:50%;background:#0d0d0f;border:2.5px solid #C29717;cursor:grab;box-shadow:0 0 5px rgba(194,151,23,0.5);\"></div>",
                iconSize: [12, 12], iconAnchor: [6, 6],
            });
            const handle = L.marker(pt, { icon: vtxIcon, draggable: true, zIndexOffset: 3000 });
            handle.on("drag", (e) => {
                const ll = e.latlng;
                setLayers(prev => {
                    const next = [...prev];
                    const pts = [...next[selectedIdx].points];
                    pts[pi] = [ll.lat, ll.lng];
                    next[selectedIdx] = { ...next[selectedIdx], points: pts };
                    redrawAll(L, leafletRef.current.overlayGroup, next);
                    return next;
                });
            });
            handle.on("click", (e) => L.DomEvent.stopPropagation(e));
            handle.addTo(editGroup);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIdx]);

    // Scroll the selected layer row into view in the right panel
    useEffect(() => {
        if (selectedIdx === null) return;
        const el = layerItemRefs.current[selectedIdx];
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [selectedIdx]);

    const layerIcon = (t) => {
        const m = { marker: "location_on", pulse: "radio_button_checked", line: "show_chart", curve: "gesture", arrow: "arrow_forward", circle: "circle", polygon: "pentagon", rect: "crop_square", text: "text_fields" };
        return m[t] || "layers";
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "#0d0d0f", display: "flex", flexDirection: "column", zIndex: 1200 }}>
            {/* ── Top bar ────────────────────────────────────────────────────── */}
            <div style={{ height: 48, background: "#111213", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", padding: "0 12px", gap: 10, flexShrink: 0, zIndex: 10 }}>
                <IconButton size="small" onClick={onBack} sx={{ color: "#888", "&:hover": { color: "#fff" } }}>
                    <Icon>arrow_back</Icon>
                </IconButton>
                <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.08)" }} />
                <img src="/icons/logo.webp" alt="FORTE" style={{ height: 20, opacity: 0.88 }} onError={e => { e.target.style.display="none"; }} />
                <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.08)" }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: "#C29717", letterSpacing: "0.16em", textTransform: "uppercase" }}>MAPS</span>
                <div style={{ width: 1, height: 14, background: "rgba(255,255,255,0.06)" }} />
                <span style={{ fontSize: 11, color: "#888", letterSpacing: "0.06em" }}>{map.title}</span>
                <div style={{ flex: 1 }} />
                {/* Satellite switcher */}
                {SAT_LAYERS.map(s => (
                    <Tooltip key={s.id} title={s.label}>
                        <button onClick={() => switchSat(s.id)} style={{
                            background: satMode === s.id ? "rgba(194,151,23,0.15)" : "transparent",
                            border: satMode === s.id ? "1px solid rgba(194,151,23,0.4)" : "1px solid rgba(255,255,255,0.06)",
                            borderRadius: 4, color: satMode === s.id ? "#C29717" : "#666",
                            fontSize: 9, padding: "3px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                        }}>
                            <Icon sx={{ fontSize: "13px !important" }}>{s.icon}</Icon>
                            <span style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase" }}>{s.label}</span>
                        </button>
                    </Tooltip>
                ))}
                <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.08)" }} />
                <Tooltip title="Coordinate grid">
                <button onClick={() => {
                    const ng = !showGrid;
                    setShowGrid(ng);
                    if (leafletRef.current) {
                        leafletRef.current._showGrid = ng;
                        if (ng) drawGraticule(leafletRef.current.map, leafletRef.current.graticuleGroup);
                        else leafletRef.current.graticuleGroup.clearLayers();
                    }
                }} style={{
                    background: showGrid ? "rgba(160,180,200,0.12)" : "transparent",
                    border: showGrid ? "1px solid rgba(160,180,200,0.4)" : "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 4, color: showGrid ? "#a0b4c8" : "#555",
                    fontSize: 9, padding: "3px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                }}>
                    <Icon sx={{ fontSize: "13px !important" }}>grid_on</Icon>
                    <span style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase" }}>GRID</span>
                </button>
                </Tooltip>
                <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.08)" }} />
                <button onClick={save} disabled={saving || !isDirty} style={{
                    background: savedMsg ? "rgba(34,197,94,0.15)" : isDirty ? "rgba(194,151,23,0.12)" : "transparent",
                    border: savedMsg ? "1px solid rgba(34,197,94,0.4)" : isDirty ? "1px solid rgba(194,151,23,0.3)" : "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 4, color: savedMsg ? "#22c55e" : isDirty ? "#C29717" : "#444",
                    fontSize: 10, padding: "4px 14px", cursor: isDirty && !saving ? "pointer" : "default", fontWeight: 600, letterSpacing: "0.08em",
                }}>
                    {savedMsg ? "✓ SAVED" : saving ? "SAVING…" : isDirty ? "SAVE ●" : "SAVED"}
                </button>
            </div>

            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {/* ── Left toolbar ───────────────────────────────────────────── */}
                <div style={{ width: 52, background: "#0d0d0f", borderRight: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "8px 0", overflowY: "auto" }}>
                    {TOOLS.map(t => (
                        <Tooltip key={t.id} title={t.label} placement="right">
                            <button onClick={() => setTool(t.id)} style={{
                                width: 38, height: 38, border: "none", borderRadius: 6, cursor: "pointer", padding: 0,
                                background: tool === t.id ? "rgba(194,151,23,0.18)" : "transparent",
                                color: tool === t.id ? "#C29717" : "#555",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                outline: tool === t.id ? "1px solid rgba(194,151,23,0.35)" : "none",
                            }}>
                                <Icon sx={{ fontSize: "18px !important" }}>{t.icon}</Icon>
                            </button>
                        </Tooltip>
                    ))}

                    <div style={{ height: 1, width: 30, background: "rgba(255,255,255,0.06)", margin: "6px 0" }} />

                    {/* Color swatches */}
                    {COLORS.map(c => (
                        <button key={c} onClick={() => setColor(c)} title={c} style={{
                            width: 18, height: 18, borderRadius: "50%", background: c, border: "none",
                            cursor: "pointer", outline: color === c ? "2px solid #C29717" : "2px solid transparent",
                            outlineOffset: 2, margin: "1px 0", padding: 0, flexShrink: 0, display: "block",
                        }} />
                    ))}
                    {/* Custom color */}
                    <input type="color" value={color} onChange={e => setColor(e.target.value)}
                        style={{ width: 28, height: 20, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, background: "transparent", cursor: "pointer", padding: 1 }} />

                    <div style={{ height: 1, width: 30, background: "rgba(255,255,255,0.06)", margin: "6px 0" }} />

                    {/* Weight */}
                    <span style={{ fontSize: 8, color: "#444", textTransform: "uppercase", letterSpacing: "0.05em" }}>W</span>
                    {[1, 2, 3, 4].map(w => (
                        <button key={w} onClick={() => setLineWeight(w)} style={{
                            width: 38, height: 20, border: "none", borderRadius: 3, cursor: "pointer",
                            background: lineWeight === w ? "rgba(194,151,23,0.18)" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <div style={{ width: 24, height: w, background: lineWeight === w ? "#C29717" : "#555", borderRadius: 1 }} />
                        </button>
                    ))}

                    <div style={{ height: 1, width: 30, background: "rgba(255,255,255,0.06)", margin: "6px 0" }} />
                    <span style={{ fontSize: 8, color: "#444", textTransform: "uppercase", letterSpacing: "0.05em" }}>LINE</span>
                    {[
                        { id: "solid",  d: "M2,10 L36,10" },
                        { id: "dashed", d: "M2,10 L11,10 M15,10 L24,10 M28,10 L36,10" },
                        { id: "dotted", d: "M2,10 L4,10 M9,10 L11,10 M16,10 L18,10 M23,10 L25,10 M30,10 L32,10" },
                    ].map(s => (
                        <button key={s.id} onClick={() => setLineStyle(s.id)} title={s.id} style={{
                            width: 38, height: 22, border: "none", borderRadius: 3, cursor: "pointer",
                            background: lineStyle === s.id ? "rgba(194,151,23,0.18)" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            outline: lineStyle === s.id ? "1px solid rgba(194,151,23,0.35)" : "none",
                        }}>
                            <svg width="36" height="20" viewBox="0 0 36 20">
                                <path d={s.d} stroke={lineStyle === s.id ? "#C29717" : "#555"} strokeWidth="2" strokeLinecap="round" fill="none"/>
                            </svg>
                        </button>
                    ))}
                </div>

                {/* ── Map ────────────────────────────────────────────────────── */}
                <div style={{ flex: 1, position: "relative" }}>
                    <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />

                    {/* Tool hint */}
                    {mouseCoords && (
                        <div style={{ position: "absolute", bottom: 16, right: 16, background: "rgba(13,13,15,0.88)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 4, padding: "4px 10px", fontSize: 10, fontFamily: "monospace", color: "#a0b4c8", pointerEvents: "none", lineHeight: 1.65, zIndex: 999 }}>
                            <div>{mouseCoords.lat >= 0 ? mouseCoords.lat.toFixed(5)+String.fromCharCode(176)+"N" : Math.abs(mouseCoords.lat).toFixed(5)+String.fromCharCode(176)+"S"}</div>
                            <div>{mouseCoords.lng >= 0 ? mouseCoords.lng.toFixed(5)+String.fromCharCode(176)+"E" : Math.abs(mouseCoords.lng).toFixed(5)+String.fromCharCode(176)+"W"}</div>
                        </div>
                    )}
                    <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", background: "rgba(13,13,15,0.85)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "4px 14px", fontSize: 10, color: "#888", pointerEvents: "none", letterSpacing: "0.06em" }}>
                        {tool === "select" && "Click a layer to select · Drag markers to move"}
                        {(tool === "marker" || tool === "pulse") && "Click to place marker"}
                        {tool === "text" && (labelText ? `Click to place "${labelText}"` : "Set label text in panel · Click to place")}
                        {(tool === "line" || tool === "curve" || tool === "arrow") && "Click to add points · Enter or dbl-click to finish · Esc cancels"}
                        {tool === "circle" && "Click center · Click edge to set radius"}
                        {(tool === "polygon" || tool === "rect") && "Click vertices · Enter or dbl-click to close · Esc cancels"}
                    </div>
                </div>

                {/* ── Right panel — layers + properties ──────────────────────── */}
                <div style={{ width: 220, background: "#0d0d0f", borderLeft: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column" }}>
                    {/* Label input */}
                    <div style={{ padding: "10px 10px 6px" }}>
                        <div style={{ fontSize: 9, color: "#444", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>Label</div>
                        <input value={labelText} onChange={e => setLabelText(e.target.value)}
                            placeholder="optional label…"
                            style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "5px 8px", color: "#ccc", fontSize: 11, outline: "none" }} />
                    </div>

                    <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "0 10px" }} />

                    {/* Layers list */}
                    <div style={{ padding: "8px 6px 6px" }}>
                        <div style={{ fontSize: 9, color: "#444", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, paddingLeft: 4 }}>
                            Layers ({layers.length})
                        </div>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", padding: "0 6px" }}>
                        {layers.length === 0 && (
                            <div style={{ fontSize: 10, color: "#333", textAlign: "center", paddingTop: 20 }}>
                                Use tools to draw on the map
                            </div>
                        )}
                        {[...layers].reverse().map((lyr, ri) => {
                            const idx = layers.length - 1 - ri;
                            const isSelected = selectedIdx === idx;
                            return (
                                <div key={idx} ref={el => { if (el) layerItemRefs.current[idx] = el; else delete layerItemRefs.current[idx]; }} onClick={() => setSelectedIdx(isSelected ? null : idx)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6, padding: "5px 6px",
                                        borderRadius: 4, cursor: "pointer", marginBottom: 2,
                                        background: isSelected ? "rgba(194,151,23,0.1)" : "rgba(255,255,255,0.02)",
                                        border: isSelected ? "1px solid rgba(194,151,23,0.25)" : "1px solid transparent",
                                    }}>
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: lyr.color, flexShrink: 0 }} />
                                    <Icon sx={{ fontSize: "13px !important", color: isSelected ? "#C29717" : "#555", flexShrink: 0 }}>
                                        {layerIcon(lyr.type)}
                                    </Icon>
                                    <span style={{ fontSize: 10, color: isSelected ? "#ddd" : "#666", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {lyr.label || lyr.type}
                                    </span>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteLayer(idx); }}
                                        sx={{ color: "#333", "&:hover": { color: "#ef4444" }, padding: "2px" }}>
                                        <Icon sx={{ fontSize: "13px !important" }}>close</Icon>
                                    </IconButton>
                                </div>
                            );
                        })}
                    </div>

                    {/* Selected layer properties */}
                    {selectedIdx !== null && layers[selectedIdx] && (
                        <div style={{ padding: "8px 10px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                            <div style={{ fontSize: 9, color: "#C29717", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                                Selected: {layers[selectedIdx].type}
                            </div>
                            <input value={layers[selectedIdx].label || ""} placeholder="label…"
                                onChange={e => setLayers(prev => {
                                    const n = [...prev];
                                    n[selectedIdx] = { ...n[selectedIdx], label: e.target.value };
                                    redrawAll(window.L, leafletRef.current.overlayGroup, n);
                                    return n;
                                })}
                                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "4px 7px", color: "#ccc", fontSize: 10, outline: "none", marginBottom: 6 }} />
                            {["marker","pulse","text","circle"].includes(layers[selectedIdx].type) && (
                                <div style={{ marginBottom: 6 }}>
                                    <div style={{ fontSize: 9, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Location</div>
                                    <div style={{ display: "flex", gap: 4 }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 8, color: "#444", marginBottom: 2, letterSpacing: "0.05em" }}>LAT</div>
                                            <input type="number" step="0.00001" value={layers[selectedIdx].lat ?? ""}
                                                onChange={e => {
                                                    const val = parseFloat(e.target.value);
                                                    if (isNaN(val)) return;
                                                    setLayers(prev => {
                                                        const n = [...prev];
                                                        n[selectedIdx] = { ...n[selectedIdx], lat: val };
                                                        redrawAll(window.L, leafletRef.current.overlayGroup, n);
                                                        return n;
                                                    });
                                                    leafletRef.current && leafletRef.current.map.setView([val, layers[selectedIdx].lng], leafletRef.current.map.getZoom());
                                                }}
                                                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "4px 5px", color: "#ccc", fontSize: 10, outline: "none" }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 8, color: "#444", marginBottom: 2, letterSpacing: "0.05em" }}>LNG</div>
                                            <input type="number" step="0.00001" value={layers[selectedIdx].lng ?? ""}
                                                onChange={e => {
                                                    const val = parseFloat(e.target.value);
                                                    if (isNaN(val)) return;
                                                    setLayers(prev => {
                                                        const n = [...prev];
                                                        n[selectedIdx] = { ...n[selectedIdx], lng: val };
                                                        redrawAll(window.L, leafletRef.current.overlayGroup, n);
                                                        return n;
                                                    });
                                                    leafletRef.current && leafletRef.current.map.setView([layers[selectedIdx].lat, val], leafletRef.current.map.getZoom());
                                                }}
                                                style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "4px 5px", color: "#ccc", fontSize: 10, outline: "none" }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {COLORS.map(c => (
                                    <button key={c} onClick={() => setLayers(prev => {
                                        const n = [...prev];
                                        n[selectedIdx] = { ...n[selectedIdx], color: c };
                                        redrawAll(window.L, leafletRef.current.overlayGroup, n);
                                        return n;
                                    })} style={{
                                        width: 14, height: 14, borderRadius: "50%", background: c, border: "none", cursor: "pointer",
                                        outline: layers[selectedIdx].color === c ? "2px solid #C29717" : "none", outlineOffset: 1,
                                    }} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
